import { setDailyStats } from "@lib/chart-utils";
import HttpClient from "@lib/http-client";

const defaultExportOptions = {
  createFile: true,
};

/**
 * @memberof Services
 */
export class ClassroomService extends HttpClient {
  getClassroomsStatsForOrg(orgCode, status) {
    const apiUrl = `/organization/${orgCode}/classroom/stats`;
    return this.http.get(apiUrl, {
      params: { include_instructors: true, status },
    });
  }

  async getClassroomsForSingleOrg(orgCode) {
    const apiUrl = `/classroom/organization/${orgCode}/classrooms`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  async deleteClassrooms({ classroomIds }) {
    const apiUrl = `/classroom/bulk/deactivation`;
    return this.http.put(apiUrl, { classroomIds });
  }

  async getClassroomsForInstructor(instructorId) {
    const apiUrl = `/classroom/instructor/${instructorId}/classrooms`;

    const data = await this.http.get(apiUrl);
    return data;
  }

  getClassroomsStatsForInstructor(instructorId, status) {
    const apiUrl = `/user/instructor/${instructorId}/classroom/stats`;
    return this.http.get(apiUrl, {
      params: { include_instructors: true, status },
    });
  }

  getMultiOrgClassroomsStats(orgCode, status) {
    const apiUrl = `/multi-organization/${orgCode}/classroom/stats`;
    return this.http.get(apiUrl, { params: { status } });
  }

  async getClassroomDetails(classroomId) {
    const apiUrl = `/classroom/${classroomId}`;
    return this.http.get(apiUrl);
  }

  async getClassroomInstructors(classroomId) {
    const apiUrl = `/classroom/${classroomId}/instructors`;
    return this.http.get(apiUrl);
  }

  async getClassroomTrainees(classroomId, status) {
    const apiUrl = `/classroom/${classroomId}/trainees`;
    return this.http.get(apiUrl, { params: { status } });
  }

  async updateClassroom(classroomId, data) {
    const apiUrl = `/classroom/${classroomId}`;
    return this.http.patch(apiUrl, { ...data });
  }

  async removeTrainee(classroomId, userId) {
    const apiUrl = `/classroom/${classroomId}/trainee/${userId}`;
    return this.http.delete(apiUrl);
  }

  async bulkRemoveTrainee(classroomId, userIds) {
    const apiUrl = `/classroom/${classroomId}/remove/trainees`;
    return this.http.put(apiUrl, userIds);
  }

  async saveClassroom(classroom) {
    const apiUrl = "/classroom/";
    return this.http.post(apiUrl, classroom);
  }

  getClassroomStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/stats/`;
    return this.http
      .post(apiUrl, { filters })
      .then((response) => response?.pop());
  }

  getClassroomDailyStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/daily/stats/`;
    return this.http
      .post(apiUrl, { filters })
      .then((response) => setDailyStats(filters, response));
  }

  getCeClassroomStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/ce/stats`;
    return this.http.post(apiUrl, { filters });
  }

  getCeClassroomDailyStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/daily/ce/stats/`;
    return this.http
      .post(apiUrl, { filters })
      .then((response) => setDailyStats(filters, response));
  }

  getProgramClassroomStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/program/stats`;
    return this.http.post(apiUrl, { filters });
  }

  getProgramClassroomDailyStats(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/daily/program/stats/`;
    return this.http
      .post(apiUrl, { filters })
      .then((response) => setDailyStats(filters, response));
  }

  getClassroomTopCareers(classroomId, filters) {
    const apiUrl = `/classroom/${classroomId}/ce/top-careers`;
    return this.http.post(apiUrl, { filters });
  }

  getClassroomCareerRatings(classroomId, learningExperienceId, filters) {
    const apiUrl = `/classroom/${classroomId}/ce/career/${learningExperienceId}/ratings`;
    return this.http.post(apiUrl, { filters });
  }

  exportClassroomStats(classroomId, filters, fileName) {
    const requestConfig = {
      method: "post",
      url: `/classroom/${classroomId}/stats/export`,
      data: { filters },
    };
    return this.downloadBlob(requestConfig, fileName);
  }

  exportClassroomCeStats(classroomId, filters, fileName, options) {
    const requestConfig = {
      method: "post",
      url: `/classroom/${classroomId}/ce/stats/export`,
      data: { filters },
    };

    const exportOptions = { ...defaultExportOptions, ...options };

    return exportOptions.createFile
      ? this.downloadBlob(requestConfig, fileName)
      : this.parseCsvResponse(requestConfig);
  }

  removeInstructor(classroomId, instructorId) {
    const apiUrl = `/classroom/${classroomId}/instructor/${instructorId}`;
    return this.http.delete(apiUrl);
  }

  removeInstructors(classroomId, userIds) {
    const apiUrl = `/classroom/${classroomId}/instructors`;
    return this.http.delete(apiUrl, { data: { user_ids: userIds } });
  }

  addTrainee(classroomId, traineeId) {
    const apiUrl = `/classroom/${classroomId}/trainee/${traineeId}`;
    return this.http.post(apiUrl);
  }

  addInstructor(classroomId, instructorId) {
    const apiUrl = `/classroom/${classroomId}/instructor/${instructorId}`;
    return this.http.post(apiUrl);
  }

  addInstructors(classroomId, userIds) {
    const apiUrl = `/classroom/${classroomId}/instructors`;
    return this.http.post(apiUrl, { user_ids: userIds });
  }

  async getQuickSignupQRCode(classroomId) {
    const apiUrl = `/classroom/${classroomId}/qr-code`;
    return this.http.get(apiUrl);
  }

  async getSimPassPDF(classroomId, productId) {
    const apiUrl = `classroom/${classroomId}/sim-pass-pdf${
      productId ? `?product_id=${productId}` : ""
    }`;

    return await this.http.get(apiUrl, {
      responseType: "blob",
    });
  }

  exportClassroomProgramStats(classroomId, filters, fileName, options) {
    const requestConfig = {
      method: "post",
      url: `/classroom/${classroomId}/program/stats/export`,
      data: { filters },
    };

    const exportOptions = { ...defaultExportOptions, ...options };

    return exportOptions.createFile
      ? this.downloadBlob(requestConfig, fileName)
      : this.parseCsvResponse(requestConfig);
  }

  async archiveClassrooms(classroomIds, organizationCode, options) {
    const data = { classroomIds, organizationCode, archiveUsers: false, ...options };
    const apiUrl = "/classroom/bulk/archive";

    return this.http.put(apiUrl, data);
  }

  async unarchiveClassrooms(classroomIds, organizationCode) {
    const apiUrl = "/classroom/bulk/unarchive";
    return this.http.put(apiUrl, { classroomIds, organizationCode });
  }
}
